import { Skeleton } from './skeleton';
import { MetricLedger } from './product-surface';
import { reportCard } from '../report/styles';
import { cn } from '../../utils';

interface DetailPageSkeletonProps {
  /** Ghost metric cells in the header ledger. */
  tiles?: number;
  /** Ghost lines in the ledger's left (description) column. */
  titleLines?: number;
  /** Number of ghost rows in the body table block. */
  rows?: number;
  className?: string;
}

function GhostBodyRow({ i }: { i: number }) {
  const widths = ['w-full', 'w-5/6', 'w-2/3', 'w-3/4', 'w-4/5'];
  return (
    <div className="flex items-center gap-4 px-3 py-2.5">
      <Skeleton className={cn('h-4', widths[i % widths.length])} />
    </div>
  );
}

/**
 * TableGhostRows — THE table-shaped ghost body: a `reportCard.section` with a
 * header line and N ghost rows. `DetailPageSkeleton` composes it under an
 * `EntityHeaderSkeleton`; import it DIRECTLY on a page that has no hero card at
 * all (e.g. `/prompts`, whose tabs render a toolbar + table and no
 * `EntityHeader`) — rendering a header shell that never appears once data lands
 * is the exact zero-shift violation rule 59 exists to prevent. Never re-inline
 * these classes.
 */
export function TableGhostRows({ rows = 6, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn(reportCard.section, className)}>
      <div className="border-b border-border px-3 py-2">
        <Skeleton className="h-3 w-32" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <GhostBodyRow key={i} i={i} />
      ))}
    </div>
  );
}

/**
 * DetailPageSkeleton — THE canonical detail-page loading shell: the header
 * `MetricLedger` in its own loading state, followed by a table-shaped ghost
 * body, in the loaded page's `space-y-3` rhythm.
 *
 * It drew an `EntityHeaderSkeleton` until 2026-08-30, which was the OLD
 * summary-card geometry (a title block over a `w-fit` grid of small tiles) —
 * and by then not one of its ~26 consumers rendered an `EntityHeader` at all.
 * Every one of them loads into a `MetricLedger`, so every one of them shifted
 * on the swap: the shell had a different height, a different left column and a
 * different tile register from the thing that replaced it, which is precisely
 * the zero-shift failure rule 59 exists to prevent. Composing the real ledger
 * means the geometry can no longer drift — a change to the ledger moves its own
 * skeleton with it.
 *
 * ```tsx
 * if (isLoading) return <DetailPageSkeleton tiles={3} rows={6} />;
 * ```
 */
export function DetailPageSkeleton({ tiles = 3, titleLines = 2, rows = 6, className }: DetailPageSkeletonProps) {
  return (
    <div className={cn('w-full space-y-3 pb-12', className)}>
      <MetricLedger
        ariaLabel="Loading"
        loading
        loadingCount={tiles}
        // The left column only renders when the ledger is given one, and every
        // consumer's loaded header has one — without this the ghost band is the
        // full-bleed no-context variant and the swap moves the metrics.
        context={
          <div className="space-y-1.5">
            {Array.from({ length: Math.max(1, titleLines) }).map((_, i) => (
              <Skeleton key={i} className={cn('h-3', i === 0 ? 'w-64' : 'w-48')} />
            ))}
          </div>
        }
      />
      <TableGhostRows rows={rows} />
    </div>
  );
}
